import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo, useRef } from "react";
import {
  Users,
  FolderKanban,
  Clock,
  ClipboardList,
  UserCheck,
  Bell,
  Target,
  User,
  LogIn,
  Eye,
  EyeOff,
  Shield,
  FileText,
  Calendar,
  KeyIcon,
  DollarSign,
  Home,
  Download,
  ChevronDown,
  ChevronUp,
  UserCog,
  ListChecks,
  Users2,
  Headset,
  Timer,
  ExternalLink,
  GitPullRequest,
  Receipt,
  CalendarDays,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
  SidebarSeparator,
} from "../components/ui/sidebar";
import {
  PtsSidebarSubNavItem,
  PtsSidebarSubNavList,
} from "../components/PtsSidebarSubNav";
import {
  ptsSidebarCollapsibleIconClass,
  ptsSidebarCollapsibleTriggerClass,
} from "../components/ptsSidebarSubNavStyles";
import { Collapsible, CollapsibleTrigger } from "../components/ui/collapsible";
import { NavLink } from "../components/NavLink";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  getAccessToken,
  getUserInfo,
  setLocalUserInfo,
} from "@/lib/storeManager";
import PTSNavigationRoutes from "../utils/navigationRoutes";
import navigationRoutes from "@/utils/navigationRoutes";
import { Dropdown } from "antd";
import { LogoutOutlined, KeyOutlined, UserOutlined } from "@ant-design/icons";
import { useUser } from "@/context/useUser";
import {
  canAccessRoute,
  isAdministrative,
  isHR,
  hasRole,
  isEmployee,
  isSuperAdmin,
  isRestrictedRole,
  isUser,
  isPTSAccount,
  isPTSSprintManager,
  isPTS_PM,
  isPTS_BA,
  isPTSIT,
  canAccessBillingModule,
  canAccessBillingInvoice,
  canAccessExpenseSummary,
  canAccessProjectSetting,
} from "../utils/rolePermissions";
import {
  AuthService,
  NotificationService,
  CURRENT_USER_DETAILS_NAVBAR_KEY,
  PtsCurrentUserDetailsService,
  getCurrentUserDisplayName,
} from "@/services/APIs";
import { useToast } from "../hooks/use-toast";
import {
  validatePassword,
  getPasswordRequirementsText,
  type PasswordValidationResult,
} from "../utils/passwordValidation";
import { useQuery } from "@tanstack/react-query";
import { PTS_IN_APP_NOTIFICATION_APP } from "../utils/ptsInAppNotifications";
import { usePtsZulipUnread } from "../context/PtsZulipUnreadContext";
import { openServiceDeskApp } from "../lib/openServiceDeskApp";

type MenuItem = {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
};

// Sidebar menu titles allowed for PTS_Account role
const PTS_ACCOUNT_MENU_TITLES = [
  "Employee Dashboard",
  "Employees",
  "Projects",
  "Change Requests",
  "Purchase Approvals",
  "Leave Request",
  "Spent Hours",
  "Attendance",
  "Attendance Report",
  "Project Summary",
  "Billing Summary",
  "Expense Summary",
  "Daily Spent Hours Report",
  "Leave Application",
  "Holiday List",
  "Designation Master",
  "Service Desk Tasks",
  "Chat",
];

// Sidebar menu titles allowed for PTS_HR role (all except Roles List and Role Permission)
const PTS_HR_MENU_TITLES = [
  "Employee Dashboard",
  "Employees",
  "Designation Master",
  "Projects",
  "Change Requests",
  "Purchase Approvals",
  "Leave Request",
  "Project Templates",
  // "Sprints",
  // "Task",
  "Spent Hours",
  "Attendance",
  "Attendance Report",
  "Employee Check-In",
  "Project Summary",
  "Billing Summary",
  "Daily Spent Hours Report",
  "Remaining Time Report",
  "Leave Application",
  "Holiday List",
  "Service Desk Tasks",
  "Chat",
];

const PTS_EMPLOYEE_MENU_TITLES = [
  "Employee Dashboard",
  "Purchase Approvals",
  "Leave Request",
  "Sprint Task Allocation",
  "Service Desk Tasks",
  "Attendance Report",
  "Chat",
];

const PTS_IT_MENU_TITLES = [...PTS_EMPLOYEE_MENU_TITLES, "Expense Summary"];

const PTS_SPRINT_MANAGER_MENU_TITLES = [
  "Employee Dashboard",
  "Purchase Approvals",
  "Leave Request",
  "Sprints",
  "Sprint Task Allocation",
  "Task",
  "Service Desk Tasks",
  "Attendance Report",
  "Chat",
];

const PTS_PM_MENU_TITLES = [
  "Employee Dashboard",
  "Projects",
  "Change Requests",
  "Purchase Approvals",
  "Leave Request",
  "Project Templates",
  "Sprints",
  "Sprint Task Allocation",
  "Task",
  "Service Desk Tasks",
  "Spent Hours",
  "Attendance",
  "Attendance Report",
  "Project Summary",
  "Remaining Time Report",
  "Resource Allocation",
  "Chat",
];

const PTS_BA_MENU_TITLES = [
  "Employee Dashboard",
  "Projects",
  "Change Requests",
  "Purchase Approvals",
  "Leave Request",
  "Project Templates",
  "Sprints",
  "Sprint Task Allocation",
  "Task",
  "Service Desk Tasks",
  "Project Summary",
  "Resource Allocation",
  "Attendance Report",
  "Chat",
];

// const CustomAvatar = () => {
//   const [userInfo, setUserInfo] = useState<any>(null);

//   useEffect(() => {
//     const user = getUserInfo();
//     setUserInfo(user);
//   }, []);

//   return (
//     <div className="flex cursor-pointer items-center gap-2">
//       <div className="bg-primary flex h-10 w-10 items-center justify-center rounded-full">
//         <span className="text-primary-foreground text-sm font-bold">
//           {userInfo?.first_name?.[0] ?? userInfo?.full_name?.[0] ?? "U"}
//         </span>
//       </div>
//       <span className="text-sm font-medium">
//         {userInfo?.first_name ?? userInfo?.full_name ?? "User"}
//       </span>
//     </div>
//   );
// };

const CustomAvatar = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useUser();
  const [userInfo, setUserInfo] = useState<any>(() => getUserInfo());
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordValidation, setPasswordValidation] =
    useState<PasswordValidationResult | null>(null);
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { toast } = useToast();

  const hasAccessToken = !!getAccessToken();

  useEffect(() => {
    setUserInfo(getUserInfo());
  }, [hasAccessToken, isAuthenticated]);

  // Run when access token exists (PTS login sets localStorage before UserContext updates)
  const { data: currentUserDetails } = useQuery({
    queryKey: [...CURRENT_USER_DETAILS_NAVBAR_KEY],
    queryFn: () => PtsCurrentUserDetailsService.getCurrentUserDetails(),
    enabled: hasAccessToken,
    staleTime: 60_000,
  });

  const navbarDisplayName = useMemo(() => {
    const fromApi = getCurrentUserDisplayName(currentUserDetails);
    if (fromApi) {
      return fromApi;
    }
    if (userInfo?.full_name?.trim()) {
      return userInfo.full_name.trim();
    }
    if (userInfo?.first_name || userInfo?.last_name) {
      return [userInfo.first_name, userInfo.last_name]
        .filter(Boolean)
        .join(" ");
    }
    return userInfo?.first_name ?? "User";
  }, [currentUserDetails, userInfo]);

  useEffect(() => {
    const name = getCurrentUserDisplayName(currentUserDetails);
    if (!name) {
      return;
    }
    const stored = getUserInfo();
    if (stored?.full_name === name) {
      return;
    }
    setLocalUserInfo({ ...(stored ?? userInfo ?? {}), full_name: name });
    setUserInfo((prev: any) => ({ ...(prev ?? {}), full_name: name }));
  }, [currentUserDetails, userInfo]);

  const avatarInitials = useMemo(() => {
    if (currentUserDetails?.initials?.trim()) {
      return currentUserDetails.initials.trim().toUpperCase();
    }
    const parts = navbarDisplayName.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return navbarDisplayName?.[0]?.toUpperCase() ?? "U";
  }, [currentUserDetails, navbarDisplayName]);

  // Avatar image from login userInfo (current-user-details has no img field)
  const avatarImageUrl = userInfo?.img ?? null;

  useEffect(() => {
    setImageError(false);
  }, [avatarImageUrl]);

  // Old API — commented out (403 after login)
  // GET /formily/dynamic-model/PTS%20Emp/{employeeId}/
  // const { data: currentEmployee } = useQuery({
  //   queryKey: ["employee-profile-navbar", EMPLOYEE_SCHEMA_NAME, employeeId],
  //   queryFn: async () => {
  //     const emp = await DynamicModuleService.getItemById(
  //       EMPLOYEE_SCHEMA_NAME,
  //       employeeId
  //     );
  //     ...
  //   },
  // });
  // const { data: employeeDataResponse } = useQuery({
  //   queryFn: () =>
  //     DynamicModuleService.getGridColItems(EMPLOYEE_SCHEMA_NAME, 1, 1000, {}),
  // });

  const handleLogout = async () => {
    await logout();
  };

  const handleResetPasswordClick = () => {
    setIsResetPasswordOpen(true);
    setPasswordValidation(null);
    setShowPasswordRequirements(false);
    setCurrentPassword("");
    setNewPassword("");
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword || !newPassword) {
      toast({
        title: "Error",
        description: "Please fill in all fields.",
        variant: "destructive",
      });
      return;
    }

    // Comprehensive password validation with user context
    const validation = validatePassword(newPassword, {
      username: userInfo?.email?.split("@")[0] ?? undefined,
      email: userInfo?.email ?? undefined,
      firstName: userInfo?.first_name ?? undefined,
      lastName: userInfo?.last_name ?? undefined,
      currentPassword: currentPassword,
    });

    setPasswordValidation(validation);

    if (!validation.isValid) {
      toast({
        title: "Password Validation Error",
        description:
          validation.errors[0] || "Password does not meet requirements",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await AuthService.resetPassword({
        current_password: currentPassword,
        new_password: newPassword,
      });

      if (response) {
        toast({
          title: "Success",
          description: "Password has been reset successfully!",
        });
        setIsResetPasswordOpen(false);
        setCurrentPassword("");
        setNewPassword("");
      }
    } catch (error: any) {
      console.error("Reset password failed:", error);
      toast({
        title: "Error",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "Failed to reset password. Please check your current password and try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsResetPasswordOpen(false);
    setCurrentPassword("");
    setNewPassword("");
    setPasswordValidation(null);
    setShowPasswordRequirements(false);
  };

  return (
    <>
      <div className="flex min-w-0 shrink items-center gap-2">
        <Dropdown
          placement="bottomRight"
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "myProfile",
                icon: <UserOutlined />,
                label: "My Profile",
                onClick: () => navigate(PTSNavigationRoutes.EMPLOYEE_PROFILE),
              },
              {
                key: "resetPassword",
                icon: <KeyOutlined />,
                label: "Reset Password",
                onClick: handleResetPasswordClick,
              },
              {
                key: "logout",
                icon: <LogoutOutlined />,
                label: "Logout",
                onClick: handleLogout,
                danger: true,
              },
            ],
          }}
        >
          <div className="flex min-w-0 cursor-pointer items-center gap-2 md:gap-3">
            <div className="bg-secondary border-primary flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 md:h-10 md:w-10">
              {avatarImageUrl && !imageError ? (
                <img
                  src={avatarImageUrl}
                  alt={navbarDisplayName}
                  className="h-full w-full object-cover"
                  onError={() => setImageError(true)}
                />
              ) : (
                <span className="text-primary text-xs font-bold uppercase">
                  {avatarInitials}
                </span>
              )}
            </div>
            <span
              className="hidden max-w-[200px] truncate text-sm font-semibold text-gray-900 sm:inline"
              style={{ fontFamily: "Nunito" }}
              title={navbarDisplayName}
            >
              {navbarDisplayName}
            </span>
          </div>
        </Dropdown>
      </div>

      <Dialog open={isResetPasswordOpen}>
        <DialogContent className="sm:max-w-[425px] [&>button]:hidden">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your current password and a new password to change your
              password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCurrentPassword(e.target.value)
                  }
                  className="bg-input-background pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {!showCurrentPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="new-password">New Password</Label>
                <button
                  type="button"
                  onClick={() =>
                    setShowPasswordRequirements(!showPasswordRequirements)
                  }
                  className="text-primary text-xs hover:underline"
                >
                  {showPasswordRequirements
                    ? "Hide Requirements"
                    : "Show Requirements"}
                </button>
              </div>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password (8-10 characters)"
                  value={newPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newPass = e.target.value;
                    setNewPassword(newPass);
                    if (newPass) {
                      const validation = validatePassword(newPass, {
                        username: userInfo?.email?.split("@")[0] ?? undefined,
                        email: userInfo?.email ?? undefined,
                        firstName: userInfo?.first_name ?? undefined,
                        lastName: userInfo?.last_name ?? undefined,
                        currentPassword: currentPassword ?? undefined,
                      });
                      setPasswordValidation(validation);
                    } else {
                      setPasswordValidation(null);
                    }
                  }}
                  className={`bg-input-background pr-10 ${
                    passwordValidation && !passwordValidation.isValid
                      ? "border-red-500 focus-visible:ring-red-500"
                      : passwordValidation?.isValid
                        ? "border-green-500 focus-visible:ring-green-500"
                        : ""
                  }`}
                  required
                  minLength={8}
                  maxLength={10}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {!showNewPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {passwordValidation && passwordValidation.errors.length > 0 && (
                <div className="rounded-md bg-red-50 p-2 text-xs text-red-800">
                  <ul className="list-disc space-y-1 pl-4">
                    {passwordValidation.errors.slice(0, 3).map(error => (
                      <li key={error}>{error}</li>
                    ))}
                    {passwordValidation.errors.length > 3 && (
                      <li>
                        ...and {passwordValidation.errors.length - 3} more
                      </li>
                    )}
                  </ul>
                </div>
              )}
              {passwordValidation &&
                passwordValidation.isValid &&
                newPassword.length >= 8 && (
                  <div className="rounded-md bg-green-50 p-2 text-xs text-green-800">
                    ✓ Password meets all requirements
                  </div>
                )}
              {showPasswordRequirements && (
                <div className="max-h-48 overflow-y-auto rounded-md bg-blue-50 p-3 text-xs text-blue-900">
                  <p className="mb-2 font-semibold">Password Requirements:</p>
                  <ul className="list-disc space-y-1 pl-4">
                    {getPasswordRequirementsText().map(req => (
                      <li key={req}>{req}</li>
                    ))}
                    <li className="mt-2 font-semibold">
                      When changing password: Must be different from current
                      password
                    </li>
                  </ul>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

const USER_MANAGEMENT_PATHS = [
  navigationRoutes.PERMISSIONS,
  navigationRoutes.ROLES,
  navigationRoutes.USERS,
];

const BILLING_PATHS = [
  PTSNavigationRoutes.BILLING_INVOICE,
  PTSNavigationRoutes.BILLING_PROJECT_SETTING,
];

const EXPENSE_PATHS = [
  PTSNavigationRoutes.EXPENSE_SUMMARY,
  PTSNavigationRoutes.EXPENSE_DASHBOARD,
];

const ROLES_AND_DESIGNATION_TITLES = [
  "Roles List",
  "Role Permission",
  "Designation Master",
] as const;

const ROLES_AND_DESIGNATION_PATHS = [
  PTSNavigationRoutes.ROLES_LIST,
  PTSNavigationRoutes.ROLE_PERMISSION_MANAGER,
  PTSNavigationRoutes.DESIGNATION_MASTER,
];

const SPRINTS_MODULE_TITLES = [
  "Sprints",
  "Sprint Task Allocation",
  "Resource Allocation",
  "Project Summary",
] as const;

const SPRINTS_MODULE_PATHS = [
  PTSNavigationRoutes.SPRINT,
  PTSNavigationRoutes.SPRINT_TASK_ALLOCATION,
  PTSNavigationRoutes.RESOURCE_ALLOCATION,
  PTSNavigationRoutes.PROJECT_SUMMARY,
];

const PTSLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const mainScrollRef = useRef<HTMLElement>(null);
  const isUserManagementPath = USER_MANAGEMENT_PATHS.some(p =>
    location.pathname.startsWith(p)
  );
  const isBillingPath = BILLING_PATHS.some(p =>
    location.pathname.startsWith(p)
  );
  const isExpensePath = EXPENSE_PATHS.some(p =>
    location.pathname.startsWith(p)
  );
  const isRolesAndDesignationPath = ROLES_AND_DESIGNATION_PATHS.some(p =>
    location.pathname.startsWith(p)
  );
  const isSprintsModulePath = SPRINTS_MODULE_PATHS.some(
    p => location.pathname === p || location.pathname.startsWith(p + "/")
  );
  const [userManagementOpen, setUserManagementOpen] =
    useState(isUserManagementPath);
  const [billingOpen, setBillingOpen] = useState(isBillingPath);
  const [expenseOpen, setExpenseOpen] = useState(isExpensePath);
  const [rolesAndDesignationOpen, setRolesAndDesignationOpen] = useState(
    isRolesAndDesignationPath
  );
  const [sprintsModuleOpen, setSprintsModuleOpen] =
    useState(isSprintsModulePath);

  useEffect(() => {
    mainScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  const { data: unreadNotificationCount } = useQuery({
    queryKey: ["in-app-unread-count", PTS_IN_APP_NOTIFICATION_APP],
    queryFn: async () => {
      const res = await NotificationService.getInAppUnreadCount({
        app: PTS_IN_APP_NOTIFICATION_APP,
      });
      const count = Number(
        res?.unread_count ?? (res as { count?: number } | undefined)?.count
      );
      if (!Number.isFinite(count)) {
        throw new Error("Invalid unread count response");
      }
      return count;
    },
    refetchInterval: 30000,
  });

  const { totalZulipChatUnread } = usePtsZulipUnread();

  useEffect(() => {
    if (isUserManagementPath) {
      setUserManagementOpen(true);
    }
  }, [isUserManagementPath]);

  useEffect(() => {
    if (isBillingPath) {
      setBillingOpen(true);
    }
  }, [isBillingPath]);

  useEffect(() => {
    if (isExpensePath) {
      setExpenseOpen(true);
    }
  }, [isExpensePath]);

  useEffect(() => {
    if (isRolesAndDesignationPath) {
      setRolesAndDesignationOpen(true);
    }
  }, [isRolesAndDesignationPath]);

  useEffect(() => {
    if (isSprintsModulePath) {
      setSprintsModuleOpen(true);
    }
  }, [isSprintsModulePath]);

  const showBillingModule = useMemo(() => canAccessBillingModule(), []);
  const showBillingInvoice = useMemo(() => canAccessBillingInvoice(), []);
  const showProjectSetting = useMemo(() => canAccessProjectSetting(), []);

  // Main menu items matching the image
  const allMainNavItems: MenuItem[] = useMemo(
    () => [
      // {
      //   title: "Dashboard",
      //   url: `${PTSNavigationRoutes.DASHBOARD}`,
      //   icon: LayoutDashboard,
      // },
      {
        title: "Employee Dashboard",
        url: `${PTSNavigationRoutes.EMPLOYEE_DASHBOARD}`,
        icon: User,
      },
      {
        title: "Attendance Report",
        url: `${PTSNavigationRoutes.MY_ATTENDANCE_REPORT}`,
        icon: UserCheck,
      },
      // {
      //   title: "Executive Dashboard",
      //   url: `${PTSNavigationRoutes.EXECUTIVE_DASHBOARD}`,
      //   icon: ChartPie,
      // },
      {
        title: "Employees",
        url: `${PTSNavigationRoutes.EMPLOYEE}`,
        icon: Users,
      },
      // {
      //   title: "Employee Rates",
      //   url: `${PTSNavigationRoutes.EMPLOYEE_RATES}`,
      //   icon: DollarSign,
      // },
      {
        title: "Projects",
        url: `${PTSNavigationRoutes.PROJECTS}`,
        icon: FolderKanban,
      },
      {
        title: "Change Requests",
        url: `${PTSNavigationRoutes.CHANGE_REQUESTS}`,
        icon: GitPullRequest,
      },
      {
        title: "Purchase Approvals",
        url: `${PTSNavigationRoutes.PURCHASE_APPROVALS}`,
        icon: Receipt,
      },
      {
        title: "Leave Request",
        url: `${PTSNavigationRoutes.LEAVE_REQUESTS}`,
        icon: CalendarDays,
      },
      // {
      //   title: "Project Templates",
      //   url: `${PTSNavigationRoutes.PROJECT_TEMPLATES}`,
      //   icon: FileText,
      // },
      {
        title: "Sprints",
        url: `${PTSNavigationRoutes.SPRINT}`,
        icon: Target,
      },
      {
        title: "Sprint Task Allocation",
        url: `${PTSNavigationRoutes.SPRINT_TASK_ALLOCATION}`,
        icon: ListChecks,
      },
      {
        title: "Resource Allocation",
        url: `${PTSNavigationRoutes.RESOURCE_ALLOCATION}`,
        icon: Users2,
      },
      {
        title: "Project Summary",
        url: `${PTSNavigationRoutes.PROJECT_SUMMARY}`,
        icon: FileText,
      },
      {
        title: "Task",
        url: `${PTSNavigationRoutes.TASKS}`,
        icon: ClipboardList,
      },
      {
        title: "Service Desk Tasks",
        url: `${PTSNavigationRoutes.SERVICE_DESK_TASKS}`,
        icon: Headset,
      },
      // {
      //   title: "Chat",
      //   url: `${PTSNavigationRoutes.CHAT}`,
      //   icon: MessageSquare,
      // },
      {
        title: "Spent Hours",
        url: `${PTSNavigationRoutes.SPENT_HOURS}`,
        icon: Clock,
      },
      {
        title: "Attendance",
        url: `${PTSNavigationRoutes.ATTENDANCE}`,
        icon: UserCheck,
      },
      {
        title: "Employee Check-In",
        url: `${PTSNavigationRoutes.EMPLOYEE_CHECKIN}`,
        icon: LogIn,
      },
      {
        title: "Billing Summary",
        url: `${PTSNavigationRoutes.BILLING_SUMMARY}`,
        icon: DollarSign,
      },
      {
        title: "Expense Summary",
        url: `${PTSNavigationRoutes.EXPENSE_SUMMARY}`,
        icon: Receipt,
      },
      {
        title: "Daily Spent Hours Report",
        url: `${PTSNavigationRoutes.DAILY_SPENT_HOURS}`,
        icon: Clock,
      },
      {
        title: "Remaining Time Report",
        url: `${PTSNavigationRoutes.REMAINING_TIME_REPORT}`,
        icon: Timer,
      },
      {
        title: "Roles List",
        url: `${PTSNavigationRoutes.ROLES_LIST}`,
        icon: Shield,
      },
      {
        title: "Role Permission",
        url: `${PTSNavigationRoutes.ROLE_PERMISSION_MANAGER}`,
        icon: KeyIcon,
      },
      {
        title: "Designation Master",
        url: `${PTSNavigationRoutes.DESIGNATION_MASTER}`,
        icon: UserCog,
      },
      {
        title: "Leave Application",
        url: `${PTSNavigationRoutes.LEAVE_APPLICATION}`,
        icon: Calendar,
      },
      {
        title: "Holiday List",
        url: `${PTSNavigationRoutes.HOLIDAY_LIST}`,
        icon: Calendar,
      },
      // {
      //   title: "Today's Hours",
      //   url: `${PTSNavigationRoutes.TODAYS_HOURS}`,
      //   icon: Calendar,
      //     },
    ],
    []
  );

  // Check if user is Super Admin
  const userIsSuperAdmin = useMemo(() => isSuperAdmin(), []);

  // Check if user is User (PTS_Admin or user role)
  const userIsUser = useMemo(() => {
    return isUser() || hasRole("PTS_Admin") || hasRole("pts_admin");
  }, []);

  // Check if user is Employee
  const userIsEmployee = useMemo(() => isEmployee(), []);

  // PTS_IT: employee menu plus Expense Summary
  const userIsPTSIT = useMemo(() => isPTSIT(), []);

  // PTS_Accounting: broad sidebar (all except roles, role permission, leave application)
  const userIsRestrictedRole = useMemo(() => isRestrictedRole(), []);

  const userIsPTS_PM = useMemo(() => isPTS_PM(), []);
  const userIsPTS_BA = useMemo(() => isPTS_BA(), []);

  // PTS_Account: limited sidebar (employee dashboard, employee, project, spent hours, attendance, project summary, billing summary, daily work item report, leave application, holiday list)
  const userIsPTSAccount = useMemo(() => isPTSAccount(), []);

  // PTS_Sprint_Manager: limited sidebar (employee dashboard, sprint, sprint task allocation, task)
  const userIsPTSSprintManager = useMemo(() => isPTSSprintManager(), []);

  const userCanAccessExpenseSummary = useMemo(
    () => canAccessExpenseSummary(),
    []
  );

  // Filter menu items based on user role permissions
  const mainNavItems: MenuItem[] = useMemo(() => {
    let items: MenuItem[];

    // Super Admin or PTS_Admin: Show all menu items
    if (userIsSuperAdmin || userIsUser) {
      items = allMainNavItems;
    }
    // PTS_IT: employee routes plus Expense Summary
    else if (userIsPTSIT) {
      items = allMainNavItems.filter(item =>
        PTS_IT_MENU_TITLES.includes(item.title)
      );
    }
    // Employee role: Show Employee Dashboard and Sprint Task Allocation
    else if (userIsEmployee) {
      items = allMainNavItems.filter(item =>
        PTS_EMPLOYEE_MENU_TITLES.includes(item.title)
      );
    }
    // PTS_Account: Show only allowed list (employeedashboard, employee, project, spent hours, attendance, project summary, billing summary, Daily Work Item Report, leave application, holiday list)
    else if (userIsPTSAccount) {
      items = allMainNavItems.filter(item =>
        PTS_ACCOUNT_MENU_TITLES.includes(item.title)
      );
    }
    // PTS_Sprint_Manager: Show only sprint-related modules
    else if (userIsPTSSprintManager) {
      items = allMainNavItems.filter(item =>
        PTS_SPRINT_MANAGER_MENU_TITLES.includes(item.title)
      );
    }
    // PTS_PM: whitelist (includes Spent Hours)
    else if (userIsPTS_PM) {
      items = allMainNavItems.filter(item =>
        PTS_PM_MENU_TITLES.includes(item.title)
      );
    }
    // PTS_BA: whitelist (no Spent Hours)
    else if (userIsPTS_BA) {
      items = allMainNavItems.filter(item =>
        PTS_BA_MENU_TITLES.includes(item.title)
      );
    }
    // PTS_Accounting: Show all except "Roles List", "Role Permission", "Leave Application"
    else if (userIsRestrictedRole) {
      items = allMainNavItems.filter(
        item =>
          item.title !== "Roles List" &&
          item.title !== "Role Permission" &&
          item.title !== "Leave Application"
      );
    }
    // HR role: Show only allowed list (same pattern as PTS_Account)
    else if (isHR()) {
      items = allMainNavItems.filter(item =>
        PTS_HR_MENU_TITLES.includes(item.title)
      );
    }
    // Administrative: Show all except "Roles List" and "Role Permission Manager"
    else if (isAdministrative()) {
      items = allMainNavItems.filter(
        item =>
          item.title !== "Roles List" &&
          item.title !== "Role Permission Manager"
      );
    }
    // Default: Only show items allowed by canAccessRoute
    else {
      items = allMainNavItems.filter(item => canAccessRoute(item.url));
    }

    // Holiday List: visible to HR, PTS_Admin, and PTS_Account
    if (!(isHR() || userIsUser || userIsPTSAccount)) {
      items = items.filter(item => item.title !== "Holiday List");
    }

    // Expense Summary: PTS_Admin, PTS_IT, and PTS_Account.
    if (!userCanAccessExpenseSummary) {
      items = items.filter(item => item.title !== "Expense Summary");
    }

    // Home, Imports, User Management are shown at the end of sidebar, only for PTS_Admin (separate block)
    return items;
  }, [
    allMainNavItems,
    userIsSuperAdmin,
    userIsEmployee,
    userIsPTSIT,
    userIsUser,
    userIsPTSAccount,
    userIsPTSSprintManager,
    userIsPTS_PM,
    userIsPTS_BA,
    userIsRestrictedRole,
    userCanAccessExpenseSummary,
  ]);

  const rolesAndDesignationItems = useMemo(
    () =>
      mainNavItems.filter(item =>
        (ROLES_AND_DESIGNATION_TITLES as readonly string[]).includes(item.title)
      ),
    [mainNavItems]
  );

  const sprintsModuleItems = useMemo(
    () =>
      mainNavItems.filter(item =>
        (SPRINTS_MODULE_TITLES as readonly string[]).includes(item.title)
      ),
    [mainNavItems]
  );

  const rolesAndDesignationAnchorTitle = rolesAndDesignationItems[0]?.title;
  const sprintsModuleAnchorTitle = sprintsModuleItems[0]?.title;

  // System/Secondary menu items
  // const secondaryNavItems: MenuItem[] = [
  //   {
  //     title: "Settings",
  //     url: `${PTSNavigationRoutes.SETTINGS_LIST}`,
  //     icon: Settings,
  //   },
  //   {
  //     title: "Roles & Permissions",
  //     url: `${PTSNavigationRoutes.ROLES_LIST}`,
  //     icon: Shield,
  //   },
  // ];

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full overflow-hidden">
        <Sidebar
          className="border-sidebar-border bg-primary border-r text-white"
          collapsible="icon"
        >
          <SidebarHeader className="border-sidebar-border/20 shrink-0 border-b px-6 py-4 group-data-[collapsible=icon]:px-2">
            <div className="flex items-center gap-2 group-data-[collapsible=icon]:justify-center">
              <div className="bg-secondary flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
                <span className="text-primary-foreground text-sm font-bold">
                  P
                </span>
              </div>
              <span className="overflow-hidden whitespace-nowrap font-semibold text-white transition-all duration-200 group-data-[collapsible=icon]:w-0 group-data-[collapsible=icon]:opacity-0">
                PTS
              </span>
            </div>
          </SidebarHeader>

          <SidebarContent className="scrollbar-thin-grey bg-primary overflow-y-auto overflow-x-hidden">
            <SidebarGroup>
              {/* <SidebarGroupLabel className="px-4 py-3 text-sm font-medium text-gray-400">
                Menu
              </SidebarGroupLabel> */}
              <SidebarGroupContent>
                <SidebarMenu className="space-y-1 pt-2">
                  {mainNavItems.map(item => {
                    const isActive = location.pathname === item.url;
                    const showChatUnreadDot =
                      item.url === PTSNavigationRoutes.CHAT &&
                      totalZulipChatUnread > 0;

                    if (
                      (
                        ROLES_AND_DESIGNATION_TITLES as readonly string[]
                      ).includes(item.title)
                    ) {
                      if (
                        item.title !== rolesAndDesignationAnchorTitle ||
                        rolesAndDesignationItems.length === 0
                      ) {
                        return null;
                      }

                      return (
                        <SidebarMenuItem key="roles-and-designation-collapsible">
                          <Collapsible
                            open={rolesAndDesignationOpen}
                            onOpenChange={setRolesAndDesignationOpen}
                          >
                            <CollapsibleTrigger asChild>
                              <SidebarMenuButton
                                tooltip="Roles and Designation"
                                className={ptsSidebarCollapsibleTriggerClass(
                                  isRolesAndDesignationPath,
                                  rolesAndDesignationOpen
                                )}
                              >
                                <div className="flex items-center gap-3">
                                  <Shield
                                    className={ptsSidebarCollapsibleIconClass(
                                      isRolesAndDesignationPath,
                                      rolesAndDesignationOpen
                                    )}
                                  />
                                  <span>Roles and Designation</span>
                                </div>
                                {rolesAndDesignationOpen ? (
                                  <ChevronUp className="h-4 w-4 text-white" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-white" />
                                )}
                              </SidebarMenuButton>
                            </CollapsibleTrigger>
                            <PtsSidebarSubNavList>
                              {rolesAndDesignationItems.map(subItem => (
                                <PtsSidebarSubNavItem
                                  key={subItem.url}
                                  to={subItem.url}
                                  isActive={
                                    location.pathname === subItem.url ||
                                    location.pathname.startsWith(
                                      `${subItem.url}/`
                                    )
                                  }
                                >
                                  {subItem.title}
                                </PtsSidebarSubNavItem>
                              ))}
                            </PtsSidebarSubNavList>
                          </Collapsible>
                        </SidebarMenuItem>
                      );
                    }

                    if (
                      (SPRINTS_MODULE_TITLES as readonly string[]).includes(
                        item.title
                      )
                    ) {
                      if (
                        item.title !== sprintsModuleAnchorTitle ||
                        sprintsModuleItems.length === 0
                      ) {
                        return null;
                      }

                      return (
                        <SidebarMenuItem key="sprints-module-collapsible">
                          <Collapsible
                            open={sprintsModuleOpen}
                            onOpenChange={setSprintsModuleOpen}
                          >
                            <CollapsibleTrigger asChild>
                              <SidebarMenuButton
                                tooltip="Sprints"
                                className={ptsSidebarCollapsibleTriggerClass(
                                  isSprintsModulePath,
                                  sprintsModuleOpen
                                )}
                              >
                                <div className="flex items-center gap-3">
                                  <Target
                                    className={ptsSidebarCollapsibleIconClass(
                                      isSprintsModulePath,
                                      sprintsModuleOpen
                                    )}
                                  />
                                  <span>Sprints</span>
                                </div>
                                {sprintsModuleOpen ? (
                                  <ChevronUp className="h-4 w-4 text-white" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-white" />
                                )}
                              </SidebarMenuButton>
                            </CollapsibleTrigger>
                            <PtsSidebarSubNavList>
                              {sprintsModuleItems.map(subItem => (
                                <PtsSidebarSubNavItem
                                  key={subItem.url}
                                  to={subItem.url}
                                  isActive={
                                    location.pathname === subItem.url ||
                                    location.pathname.startsWith(
                                      `${subItem.url}/`
                                    )
                                  }
                                >
                                  {subItem.title}
                                </PtsSidebarSubNavItem>
                              ))}
                            </PtsSidebarSubNavList>
                          </Collapsible>
                        </SidebarMenuItem>
                      );
                    }

                    if (item.title === "Expense Summary") {
                      return (
                        <SidebarMenuItem key="expense-summary-collapsible">
                          <Collapsible
                            open={expenseOpen}
                            onOpenChange={setExpenseOpen}
                          >
                            <CollapsibleTrigger asChild>
                              <SidebarMenuButton
                                tooltip="Expense Summary"
                                className={ptsSidebarCollapsibleTriggerClass(
                                  isExpensePath,
                                  expenseOpen
                                )}
                              >
                                <div className="flex items-center gap-3">
                                  <Receipt
                                    className={ptsSidebarCollapsibleIconClass(
                                      isExpensePath,
                                      expenseOpen
                                    )}
                                  />
                                  <span>Expense Summary</span>
                                </div>
                                {expenseOpen ? (
                                  <ChevronUp className="h-4 w-4 text-white" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-white" />
                                )}
                              </SidebarMenuButton>
                            </CollapsibleTrigger>
                            <PtsSidebarSubNavList>
                              <PtsSidebarSubNavItem
                                to={PTSNavigationRoutes.EXPENSE_DASHBOARD}
                                isActive={location.pathname.startsWith(
                                  PTSNavigationRoutes.EXPENSE_DASHBOARD
                                )}
                              >
                                Dashboard
                              </PtsSidebarSubNavItem>
                              <PtsSidebarSubNavItem
                                to={PTSNavigationRoutes.EXPENSE_SUMMARY}
                                isActive={
                                  location.pathname ===
                                  PTSNavigationRoutes.EXPENSE_SUMMARY
                                }
                              >
                                Expense Summary
                              </PtsSidebarSubNavItem>
                            </PtsSidebarSubNavList>
                          </Collapsible>
                        </SidebarMenuItem>
                      );
                    }

                    return (
                      <SidebarMenuItem key={item.url}>
                        <SidebarMenuButton asChild tooltip={item.title}>
                          <NavLink
                            to={item.url}
                            end={
                              item.url ===
                              PTSNavigationRoutes.EMPLOYEE_DASHBOARD
                            }
                            className={`relative flex items-center gap-3 rounded-full px-4 py-2.5 transition-colors ${
                              isActive
                                ? "text-secondary bg-[#334155] font-medium"
                                : "text-white hover:bg-[#334155]/50"
                            }`}
                            activeClassName="bg-[#334155] text-secondary font-medium"
                          >
                            <item.icon
                              className={`h-5 w-5 ${isActive ? "text-secondary" : "text-white"}`}
                            />
                            <span>{item.title}</span>
                            {showChatUnreadDot ? (
                              <span
                                className="absolute right-3 top-3 h-2.5 w-2.5 shrink-0 rounded-full bg-[#ff9d00] ring-2 ring-[#0b2f57]"
                                aria-label="Unread chat messages"
                              />
                            ) : null}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                  {showBillingModule && (
                    <SidebarMenuItem>
                      <Collapsible
                        open={billingOpen}
                        onOpenChange={setBillingOpen}
                      >
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton
                            tooltip="Billing"
                            className={ptsSidebarCollapsibleTriggerClass(
                              isBillingPath,
                              billingOpen
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <DollarSign
                                className={ptsSidebarCollapsibleIconClass(
                                  isBillingPath,
                                  billingOpen
                                )}
                              />
                              <span>Billing</span>
                            </div>
                            {billingOpen ? (
                              <ChevronUp className="h-4 w-4 text-white" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-white" />
                            )}
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <PtsSidebarSubNavList>
                          {showBillingInvoice && (
                            <PtsSidebarSubNavItem
                              to={PTSNavigationRoutes.BILLING_INVOICE}
                              isActive={location.pathname.startsWith(
                                PTSNavigationRoutes.BILLING_INVOICE
                              )}
                            >
                              Billing Invoice
                            </PtsSidebarSubNavItem>
                          )}
                          {showProjectSetting && (
                            <PtsSidebarSubNavItem
                              to={PTSNavigationRoutes.BILLING_PROJECT_SETTING}
                              isActive={location.pathname.startsWith(
                                PTSNavigationRoutes.BILLING_PROJECT_SETTING
                              )}
                            >
                              Project Setting
                            </PtsSidebarSubNavItem>
                          )}
                        </PtsSidebarSubNavList>
                      </Collapsible>
                    </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {/* PTS_Admin only: Home, Imports, User Management at the end, separated */}
            {userIsUser && (
              <SidebarGroup className="mb-8 pt-2">
                <SidebarGroupContent>
                  <SidebarSeparator className="mx-4 bg-white/20" />
                  <SidebarMenu className="mt-2 space-y-1">
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild tooltip="Home">
                        <NavLink
                          to={navigationRoutes.SCHEMAS}
                          end
                          className={`flex items-center gap-3 rounded-full px-4 py-2.5 transition-colors ${
                            location.pathname === navigationRoutes.SCHEMAS
                              ? "text-secondary bg-[#334155] font-medium"
                              : "text-white hover:bg-[#334155]/50"
                          }`}
                          activeClassName="bg-[#334155] text-secondary font-medium"
                        >
                          <Home
                            className={`h-5 w-5 ${location.pathname === navigationRoutes.SCHEMAS ? "text-secondary" : "text-white"}`}
                          />
                          <span>Home</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild tooltip="Imports">
                        <NavLink
                          to={navigationRoutes.IMPORTS}
                          className={`flex items-center gap-3 rounded-full px-4 py-2.5 transition-colors ${
                            location.pathname.startsWith(
                              navigationRoutes.IMPORTS
                            )
                              ? "text-secondary bg-[#334155] font-medium"
                              : "text-white hover:bg-[#334155]/50"
                          }`}
                          activeClassName="bg-[#334155] text-secondary font-medium"
                        >
                          <Download
                            className={`h-5 w-5 ${location.pathname.startsWith(navigationRoutes.IMPORTS) ? "text-secondary" : "text-white"}`}
                          />
                          <span>Imports</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <Collapsible
                        open={userManagementOpen}
                        onOpenChange={setUserManagementOpen}
                      >
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton
                            tooltip="User Management"
                            className={ptsSidebarCollapsibleTriggerClass(
                              isUserManagementPath,
                              userManagementOpen
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <UserCog
                                className={ptsSidebarCollapsibleIconClass(
                                  isUserManagementPath,
                                  userManagementOpen
                                )}
                              />
                              <span>User Management</span>
                            </div>
                            {userManagementOpen ? (
                              <ChevronUp className="h-4 w-4 text-white" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-white" />
                            )}
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <PtsSidebarSubNavList>
                          <PtsSidebarSubNavItem
                            to={navigationRoutes.PERMISSIONS}
                            isActive={location.pathname.startsWith(
                              navigationRoutes.PERMISSIONS
                            )}
                          >
                            Permission
                          </PtsSidebarSubNavItem>
                          <PtsSidebarSubNavItem
                            to={navigationRoutes.ROLES}
                            isActive={
                              location.pathname === navigationRoutes.ROLES ||
                              location.pathname.startsWith(
                                navigationRoutes.ROLES + "/"
                              )
                            }
                          >
                            Roles
                          </PtsSidebarSubNavItem>
                          <PtsSidebarSubNavItem
                            to={navigationRoutes.USERS}
                            isActive={
                              location.pathname === navigationRoutes.USERS ||
                              location.pathname.startsWith(
                                navigationRoutes.USERS + "/"
                              )
                            }
                          >
                            Users
                          </PtsSidebarSubNavItem>
                        </PtsSidebarSubNavList>
                      </Collapsible>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}

            {/* <SidebarGroup>
              <SidebarGroupContent>
                <SidebarGroupLabel className="px-4 py-3 text-sm font-medium text-gray-400">
                  System
                </SidebarGroupLabel>
                <SidebarMenu className="space-y-1">
                  {secondaryNavItems.map(item => {
                    const isActive = location.pathname === item.url;
                    return (
                      <SidebarMenuItem key={item.url}>
                        <SidebarMenuButton asChild tooltip={item.title}>
                          <NavLink
                            to={item.url}
                            className={`flex items-center gap-3 rounded-full px-4 py-2.5 transition-colors ${
                              isActive
                                ? "text-secondary bg-[#334155] font-medium"
                                : "text-white hover:bg-[#334155]/50"
                            }`}
                            activeClassName="bg-[#334155] text-secondary font-medium"
                          >
                            <item.icon
                              className={`h-5 w-5 ${isActive ? "text-secondary" : "text-white"}`}
                            />
                            <span>{item.title}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup> */}
          </SidebarContent>
        </Sidebar>

        <SidebarInset className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <header className="border-border sticky top-0 z-50 shrink-0 border-b bg-white shadow-sm">
            <div className="flex h-14 min-w-0 items-center justify-between gap-2 pe-3 ps-2 sm:h-16 sm:gap-3 sm:pe-6 sm:ps-3 md:gap-5">
              <div className="flex shrink-0 items-center gap-2 sm:gap-4">
                <SidebarTrigger className="h-9 w-9 shrink-0 sm:h-10 sm:w-10" />
                {/* <div className="relative w-full max-w-md">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search..."
                    className="h-10 rounded-lg border border-gray-200 bg-white pl-10"
                  />
                </div> */}
              </div>
              <div className="flex min-w-0 items-center justify-end gap-1.5 sm:gap-3 md:gap-5">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-primary/30 text-primary hover:bg-primary/5 hover:text-primary flex shrink-0 items-center gap-2 rounded-lg px-2 sm:px-3"
                  onClick={() => openServiceDeskApp({ sameTab: false })}
                  title="Open Service Desk"
                  aria-label="Open Service Desk"
                >
                  <ExternalLink className="h-4 w-4 shrink-0" />
                  <span>Open Service Desk</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:bg-secondary relative h-9 w-9 shrink-0 sm:h-[2.2rem] sm:w-[2.2rem]"
                  onClick={() => navigate(PTSNavigationRoutes.NOTIFICATIONS)}
                  aria-label="Open notifications"
                >
                  <Bell className="h-5 w-5" />
                  {(unreadNotificationCount ?? 0) > 0 && (
                    <span className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-red-500" />
                  )}
                </Button>
                <CustomAvatar />
              </div>
            </div>
          </header>

          <main
            ref={mainScrollRef}
            className="scrollbar-thin-grey flex-1 overflow-auto bg-gray-50 md:p-6"
          >
            <Outlet />
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default PTSLayout;
